<script setup lang="ts">import { ref as _ref, computed as _computed } from 'vue';

// One editable blank inside the madlibs sentence. It sizes itself to its content
// so the sentence reflows like prose instead of holding fixed-width boxes, and it
// carries no concierge knowledge — the parent owns which signal the text lands in.
type BlankTone = 'recipient' | 'city' | 'interest' | 'occasion'

withDefaults(defineProps<{
  label: string
  tone: BlankTone
  placeholder: string
  // The full completed value the parent thinks is being typed; this component
  // shows whatever of it hasn't been typed yet.
  suggestion?: string
  // Width buffer for the empty state: the blank holds this string's width while it
  // has no content, so the rotating placeholder animates inside a still box
  // instead of re-wrapping the sentence every few letters.
  reserve?: string
}>(), { suggestion: '',reserve: '', })

const model = defineModel<string>({ required: true })

// The parent needs the focus state too: it pauses this blank's placeholder
// rotation while the buyer is in the field.
const emit = defineEmits<{
  focusChange: [focused: boolean]
  accepted: []
}>()

const inputRef = ref<HTMLInputElement>()

// The parent moves focus along after a suggestion is taken, so it needs a way in.
defineExpose({ focus: () => inputRef.value?.focus() })

let focused = _ref(false)

function setFocused (value: boolean) {
  focused.value = value
  widthFloor.value = value ? model.value : ''
  emit('focusChange', value)
}

// The untyped tail of the suggestion, greyed in behind the caret. Case-insensitive
// so "sea" still completes "Seattle", and empty whenever the buyer has typed past
// the suggestion.
const ghost = _computed(() => {
  const typed = model.value
  if (!typed || !__props.suggestion) return ''
  return __props.suggestion.toLowerCase().startsWith(typed.toLowerCase()) ? __props.suggestion.slice(typed.length) : ''
})

// What the blank has to be wide enough to show: typed text plus any ghost, or the
// reserve when it's empty — the full width of the longest example it rotates
// through, held whether or not the blank is focused, so neither the placeholder
// animation nor clicking in moves the sentence.
const shownText = _computed(() => (model.value ? model.value + ghost.value : __props.reserve || __props.placeholder))

// While the buyer is in the blank, its width only ratchets up — backspacing, a
// ghost appearing and vanishing, or a spelling fix must not bounce the sentence's
// line ending under their caret. Held as a string the sizer renders invisibly (a
// second measured candidate), so no DOM measurement is needed. Cleared on blur:
// leaving the blank settles it to its content in one reflow, at a natural pause.
let widthFloor = _ref('')

watchEffect(() => {
  if (!focused.value) return
  const current = model.value + ghost.value
  if (current.length > widthFloor.value.length) widthFloor.value = current
})

function acceptSuggestion (event: KeyboardEvent) {
  if (!ghost.value) return

  // Only swallow the key when there's actually a suggestion to take, so Tab still
  // moves between blanks normally and the arrow still moves the caret.
  event.preventDefault()
  model.value = __props.suggestion
  emit('accepted')
}

// Padding tapers slightly with content length, so a blank's width travels a
// little less than its text does as placeholders cycle.
//
// Kept mild, and flat below MIN_CHARS, for a specific reason: the sizer can't
// shrink past its own min-width, so once the text is that short the padding is
// the only thing still changing — and a strong taper made the blank visibly grow
// while erasing its last few characters, which is backwards.
const MIN_PADDING = 11
const MAX_PADDING = 18
// Roughly where .blank__sizer's min-width floor lands. Approximate by nature —
// the exact character count depends on the glyphs — so it's set one wide to keep
// the tail flat rather than a hair over.
const MIN_CHARS = 4
const TAPER_CHARS = 18

const blankStyle = _computed(() => {
  // The floor counts too: padding feeds the width, so tapering it off the live
  // text while the floor holds the sizer would still wiggle the blank.
  const length = Math.max(shownText.value.length, widthFloor.value.length, MIN_CHARS)
  const taper = Math.min(1, (length - MIN_CHARS) / (TAPER_CHARS - MIN_CHARS))
  return { '--blank-padding': `${MAX_PADDING - (MAX_PADDING - MIN_PADDING) * taper}px` }
})
</script>

<template>
  <span
    class="blank"
    :class="[`blank--${tone}`, { 'blank--focused': focused, 'blank--empty': !model }]"
    :style="blankStyle"
  >
    <!-- The label is a focus-time affordance only: four permanent captions read as
         a form, which is the opposite of the sentence we're going for. The input
         keeps its aria-label either way. -->
    <span v-if="focused" class="blank__label" aria-hidden="true">{{ label }}</span>
    <span class="blank__field">
      <!-- Invisible twin: it reserves the width the text needs so the absolutely
           positioned input can grow with what's typed. Stacked spans measure the
           live text and the focus-time floor at once — the wider one wins. -->
      <span class="blank__sizer" aria-hidden="true"><span>{{ shownText }}</span><span
        v-if="widthFloor"
      >{{ widthFloor }}</span></span>
      <!-- The typed half is transparent — it only pushes the visible tail out to
           sit right after the caret. -->
      <span v-if="ghost" class="blank__ghost" aria-hidden="true"><span
        class="blank__ghost-typed"
      >{{ model }}</span>{{ ghost }}</span>
      <input
        ref="inputRef"
        v-model="model"
        type="text"
        class="blank__input"
        autocomplete="off"
        :aria-label="label"
        :placeholder="focused ? '' : placeholder"
        @focus="setFocused(true)"
        @blur="setFocused(false)"
        @keydown.tab="acceptSuggestion"
        @keydown.right="acceptSuggestion"
      >
    </span>
  </span>
</template>

<style lang="scss" scoped>
// The four tones are a categorical code — one hue per signal — so the buyer can
// tell at a glance which part of the sentence they're editing. There are no
// coral/teal semantic tokens yet, so each tone points at the nearest existing
// surface + text pair rather than inventing hex values.
.blank {
  --blank-accent: var(--color-text-secondary-dark);
  --blank-surface: var(--color-bg-surface-grey);

  // Overridden inline per content length; this is the fallback.
  --blank-padding: 12px;

  background: var(--blank-surface);

  // Not a pill: at a radius of half the height the corners re-shape as the blank
  // resizes to its text, so an erasing placeholder morphs toward a lozenge. A
  // fixed radius holds still, and reads as a field rather than a static tag.
  border-radius: 8px;
  color: var(--blank-accent);
  display: inline-block;
  font-weight: 600;

  // Its own line-height, deliberately not the sentence's. Inheriting that made
  // the box grow in lockstep with the line spacing, so raising the sentence's
  // line-height moved the rows apart and fattened the blanks by the same amount
  // — the gap between wrapped rows never actually opened up.
  // 1.35 × 21px text + 10px above and below lands the box at 48px.
  line-height: 1.35;
  padding: 10px var(--blank-padding);
  position: relative;
  transition: box-shadow 0.15s ease;
}

// An empty blank advertises itself as fillable; a filled one settles down.
.blank--empty {
  box-shadow: inset 0 -2px 0 -1px var(--blank-accent);
}

// Inset, so the focus ring can never cover the label sitting above the pill.
.blank--focused {
  box-shadow: inset 0 0 0 2px var(--blank-accent);
}

.blank--recipient {
  --blank-accent: var(--red-400);
  --blank-surface: var(--color-bg-surface-red);
}

.blank--city {
  --blank-accent: var(--blue-400);
  --blank-surface: var(--color-bg-surface-blue);
}

.blank--interest {
  --blank-accent: var(--rive-accent-foreground);
  --blank-surface: var(--color-bg-surface-pink);
}

.blank--occasion {
  --blank-accent: var(--yellow-500);
  --blank-surface: var(--color-bg-surface-yellow);
}

.blank__label {
  font-size: 9px;
  font-weight: 700;
  left: var(--blank-padding);
  letter-spacing: 0.08em;
  line-height: 1;
  position: absolute;
  text-transform: uppercase;

  // Fixed px, not em: it has to fit in the gap between rows, which is set in px
  // against the blank's height. At 9px tall this leaves 2px clear of the row above.
  top: -10px;
}

.blank__field {
  display: inline-block;
  position: relative;
}

// Its stacked children overlap in one grid cell, so the sizer is exactly as wide
// as the widest of them.
.blank__sizer {
  display: inline-grid;
  min-width: 3ch;
  visibility: hidden;

  > span {
    grid-area: 1 / 1;
    white-space: pre;
  }
}

// Shares the input's box so the tail lands exactly after what's typed.
.blank__ghost {
  inset: 0;
  opacity: 0.38;
  pointer-events: none;
  position: absolute;
  white-space: pre;
}

.blank__ghost-typed {
  color: transparent;
}

// Classed rather than a bare `input` element selector: even scoped, an element
// selector is low-specificity and app-wide input styles could cascade over it.
.blank__input {
  background: transparent;
  border: 0;
  color: inherit;
  font: inherit;
  inset: 0;
  padding: 0;
  position: absolute;
  width: 100%;

  &::placeholder {
    color: inherit;
    opacity: 0.38;
  }

  &:focus-visible {
    outline: none;
  }
}
</style>
